import { isFinalizedAttendance } from "../../../LambdaFunctions/shared/lessonAttendance";
import { ATTENDANCE_STATUS, hasRecordedAttendanceDecision } from "./lessonAttendanceStatus";
import { buildLessonRegisterPayload } from "./lessonRegisterPayloads";

export const ATTENDANCE_DECISIONS = {
  UNMARKED: "unmarked",
  PRESENT: "present",
  ABSENT: "absent",
  CANCELLED_ON_TIME: "cancelledOnTime",
  CANCELLED_LATE: "cancelledLate",
};

const cancellationStatuses = [
  ATTENDANCE_DECISIONS.CANCELLED_ON_TIME,
  ATTENDANCE_DECISIONS.CANCELLED_LATE,
];

const getLessonDetails = scheduledLesson => (
  scheduledLesson.lessonRegister
    ? {
      ...(scheduledLesson.defaultLessonDetails || {}),
      ...(scheduledLesson.lessonRegister.lessonDetails || {}),
      lessonSummary: (scheduledLesson.lessonRegister.lessonDetails || {}).lessonSummary || "",
    }
    : {
      ...(scheduledLesson.defaultLessonDetails || {}),
      lessonSummary: (scheduledLesson.defaultLessonDetails || {}).lessonSummary || "",
    }
);

const getRegisterStudentById = scheduledLesson => (
  ((scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [])
    .reduce((studentsById, student) => ({
      ...studentsById,
      [student.id]: student,
    }), {})
);

const getDefaultParticipants = scheduledLesson => (
  scheduledLesson.defaultStudents || []
);

const getParticipants = (scheduledLesson) => {
  if (isFinalizedAttendance(scheduledLesson.lessonRegister)) {
    return scheduledLesson.lessonRegister.students.map(participant => (
      (scheduledLesson.defaultStudents || []).find(student => student.id === participant.id) || participant
    ));
  }
  const defaultParticipants = getDefaultParticipants(scheduledLesson);
  const registerStudents = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [];
  const defaultParticipantIds = defaultParticipants.map(student => student.id);
  const registerOnlyParticipants = registerStudents
    .filter(student => !defaultParticipantIds.includes(student.id));

  return [
    ...defaultParticipants,
    ...registerOnlyParticipants,
  ];
};

const isHistoricalCheckedRegister = (scheduledLesson, lessonDetails) => (
  lessonDetails.attendanceStatus === ATTENDANCE_STATUS.CHECKED ||
  (
    scheduledLesson.lessonRegister &&
    !lessonDetails.attendanceStatus &&
    ((scheduledLesson.lessonRegister.students || []).length > 0)
  )
);

const mapStudentToDecision = (student, registerStudent, attendanceStatus, checkedRegister) => {
  if (registerStudent && cancellationStatuses.includes(registerStudent.participationStatus)) {
    return {
      studentId: student.id,
      decision: registerStudent.participationStatus,
      participationStatus: registerStudent.participationStatus,
      ...(registerStudent.cancellation ? { cancellation: registerStudent.cancellation } : {}),
    };
  }

  const hasExplicitDecision = registerStudent && ["attended", "absent"].includes(registerStudent.participationStatus);
  if ((checkedRegister || hasExplicitDecision) && hasRecordedAttendanceDecision(registerStudent)) {
    return {
      studentId: student.id,
      decision: registerStudent.isPresent ? ATTENDANCE_DECISIONS.PRESENT : ATTENDANCE_DECISIONS.ABSENT,
    };
  }

  if (attendanceStatus === ATTENDANCE_STATUS.CANCELLED && registerStudent) {
    return {
      studentId: student.id,
      decision: ATTENDANCE_DECISIONS.ABSENT,
    };
  }

  return {
    studentId: student.id,
    decision: ATTENDANCE_DECISIONS.UNMARKED,
  };
};

export const buildAttendanceDraft = (scheduledLesson) => {
  const lessonDetails = getLessonDetails(scheduledLesson);
  const registerStudentsById = getRegisterStudentById(scheduledLesson);
  const participants = getParticipants(scheduledLesson);
  const checkedRegister = isHistoricalCheckedRegister(scheduledLesson, lessonDetails);
  const decisions = participants.reduce((draftDecisions, student) => ({
    ...draftDecisions,
    [student.id]: mapStudentToDecision(
      student,
      registerStudentsById[student.id],
      lessonDetails.attendanceStatus,
      checkedRegister,
    ),
  }), {});

  return {
    lesson: scheduledLesson,
    lessonDetails,
    participants,
    decisions,
  };
};

export const rebuildAttendanceDraftPreservingDecisions = (currentDraft, scheduledLesson) => {
  const nextDraft = buildAttendanceDraft(scheduledLesson);

  if (!currentDraft) {
    return nextDraft;
  }

  return {
    ...nextDraft,
    lessonDetails: {
      ...nextDraft.lessonDetails,
      lessonSummary: (currentDraft.lessonDetails || {}).lessonSummary || "",
    },
    decisions: nextDraft.participants.reduce((decisions, student) => ({
      ...decisions,
      [student.id]: currentDraft.decisions[student.id] || nextDraft.decisions[student.id],
    }), {}),
  };
};

export const getAttendanceSummary = draft => (
  Object.keys(draft.decisions).reduce((summary, studentId) => {
    const { decision } = draft.decisions[studentId];
    return {
      ...summary,
      present: summary.present + (decision === ATTENDANCE_DECISIONS.PRESENT ? 1 : 0),
      absent: summary.absent + (decision === ATTENDANCE_DECISIONS.ABSENT ? 1 : 0),
      cancelledOnTime: summary.cancelledOnTime + (decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME ? 1 : 0),
      cancelledLate: summary.cancelledLate + (decision === ATTENDANCE_DECISIONS.CANCELLED_LATE ? 1 : 0),
      unmarked: summary.unmarked + (decision === ATTENDANCE_DECISIONS.UNMARKED ? 1 : 0),
    };
  }, {
    total: Object.keys(draft.decisions).length,
    present: 0,
    absent: 0,
    cancelledOnTime: 0,
    cancelledLate: 0,
    unmarked: 0,
  })
);

export const validateAttendanceDraft = (draft) => {
  const unmarkedStudentIds = Object.keys(draft.decisions)
    .filter(studentId => draft.decisions[studentId].decision === ATTENDANCE_DECISIONS.UNMARKED);

  return {
    isValid: unmarkedStudentIds.length === 0,
    unmarkedCount: unmarkedStudentIds.length,
    unmarkedStudentIds,
  };
};

export const applyAttendanceDecision = (draft, studentId, decision) => ({
  ...draft,
  decisions: {
    ...draft.decisions,
    [studentId]: {
      studentId,
      decision,
      ...(cancellationStatuses.includes(decision) && draft.decisions[studentId].cancellation
        ? { cancellation: draft.decisions[studentId].cancellation }
        : {}),
      ...(cancellationStatuses.includes(decision)
        ? { participationStatus: decision }
        : {}),
    },
  },
});

export const markAllPresent = draft => ({
  ...draft,
  decisions: Object.keys(draft.decisions).reduce((nextDecisions, studentId) => {
    const currentDecision = draft.decisions[studentId];
    if (cancellationStatuses.includes(currentDecision.decision)) {
      return {
        ...nextDecisions,
        [studentId]: currentDecision,
      };
    }
    return {
      ...nextDecisions,
      [studentId]: {
        studentId,
        decision: ATTENDANCE_DECISIONS.PRESENT,
      },
    };
  }, {}),
});

const decisionToLessonRegisterStudent = decision => ({
  id: decision.studentId,
  isPresent: decision.decision === ATTENDANCE_DECISIONS.PRESENT,
  ...(cancellationStatuses.includes(decision.decision) ? { participationStatus: decision.decision } : {}),
  ...(decision.cancellation ? { cancellation: decision.cancellation } : {}),
});

export const buildAttendanceSavePayload = (draft, role, userTeacherId) => (
  buildLessonRegisterPayload({
    lesson: draft.lesson,
    lessonDetailsEx: draft.lessonDetails,
    studentsEx: draft.participants.map(student => decisionToLessonRegisterStudent(draft.decisions[student.id])),
    role,
    userTeacherId,
  })
);
